import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useLang } from "../contexts/LangContext";
import { useTheme } from "../contexts/ThemeContext";
import Logo from "./Logo";
import {
  Sun, Moon, Languages, LogOut, Upload as UploadIcon, FolderOpen,
  LayoutGrid, Menu, X, ArrowUpRight,
} from "lucide-react";
import { useState } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "./ui/dropdown-menu";

export default function Navbar() {
  const { user, logout } = useAuth();
  const { t, lang, toggle: toggleLang } = useLang();
  const { theme, toggle: toggleTheme } = useTheme();
  const nav = useNavigate();
  const loc = useLocation();
  const [mobile, setMobile] = useState(false);

  const isAuth = user && typeof user === "object";
  const onPath = (p) => loc.pathname === p;

  const NavLink = ({ to, label, testid, icon: Icon }) => (
    <Link
      to={to}
      className={`group inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${
        onPath(to)
          ? "bg-[hsl(var(--ink))] text-[hsl(var(--bg))]"
          : "text-[hsl(var(--ink-2))] hover:bg-[hsl(var(--surface-2))]"
      }`}
      data-testid={testid}
    >
      {Icon && <Icon className="h-3.5 w-3.5" />}
      {label}
    </Link>
  );

  return (
    <header
      className="sticky top-0 z-50 border-b backdrop-blur-md"
      style={{
        backgroundColor: "hsl(var(--bg) / 0.85)",
        borderColor: "hsl(var(--border))",
      }}
      data-testid="main-navbar"
    >
      <div className="mx-auto flex max-w-[1400px] items-center justify-between px-5 py-3 sm:px-8">
        <Link to="/" className="group flex items-center gap-2.5" data-testid="nav-logo">
          <Logo size={36} color="hsl(var(--ink))" className="transition-transform group-hover:rotate-[-8deg]" />
          <div className="leading-none">
            <div className="font-display text-lg tracking-tight text-ink">606MEDIADOCS</div>
            <div className="mt-0.5 text-[9px] font-semibold uppercase tracking-[0.22em] text-muted-fg">
              media · library
            </div>
          </div>
        </Link>

        <nav className="hidden items-center gap-1 md:flex">
          <NavLink to="/" label={t("home")} testid="nav-home" />
          <NavLink to="/browse" label={t("browse")} testid="nav-browse" icon={FolderOpen} />
          {isAuth && (
            <NavLink to="/my-uploads" label={t("myUploads")} testid="nav-my-uploads" icon={LayoutGrid} />
          )}
        </nav>

        <div className="hidden items-center gap-1.5 md:flex">
          <button
            onClick={toggleTheme}
            className="rounded-full p-2 text-[hsl(var(--ink-2))] transition-colors hover:bg-[hsl(var(--surface-2))]"
            data-testid="theme-toggle"
            title={theme === "dark" ? t("light") : t("dark")}
          >
            {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>
          <button
            onClick={toggleLang}
            className="inline-flex items-center gap-1.5 rounded-full p-2 text-[hsl(var(--ink-2))] transition-colors hover:bg-[hsl(var(--surface-2))]"
            data-testid="lang-toggle"
            title={lang === "en" ? "العربية" : "English"}
          >
            <Languages className="h-4 w-4" />
            <span className="text-xs font-bold uppercase">{lang === "en" ? "AR" : "EN"}</span>
          </button>

          <span className="mx-1 h-5 w-px bg-[hsl(var(--border))]" />

          {isAuth ? (
            <>
              <Link to="/upload" className="btn btn-accent !px-4 !py-2 text-xs" data-testid="nav-upload">
                <UploadIcon className="h-3.5 w-3.5" />
                {t("upload")}
              </Link>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="ms-1 flex h-9 w-9 items-center justify-center rounded-full border text-xs font-bold uppercase"
                    style={{
                      backgroundColor: "hsl(var(--surface))",
                      borderColor: "hsl(var(--border))",
                      color: "hsl(var(--ink))",
                    }}
                    data-testid="user-menu-trigger"
                  >
                    {(user.name || user.email)[0]}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-2 py-1.5 text-xs">
                    <div className="font-semibold text-ink">{user.name || "User"}</div>
                    <div className="truncate text-muted-fg">{user.email}</div>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => nav("/upload")} data-testid="menu-upload">
                    <UploadIcon className="me-2 h-4 w-4" />
                    {t("upload")}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => nav("/my-uploads")} data-testid="menu-my-uploads">
                    <LayoutGrid className="me-2 h-4 w-4" />
                    {t("myUploads")}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={async () => {
                      await logout();
                      nav("/");
                    }}
                    data-testid="menu-logout"
                  >
                    <LogOut className="me-2 h-4 w-4" />
                    {t("signOut")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <>
              <Link to="/login" className="btn btn-ghost !px-4 !py-2 text-xs" data-testid="nav-signin">
                {t("signIn")}
              </Link>
              <Link to="/register" className="btn btn-primary !px-4 !py-2 text-xs" data-testid="nav-create-account">
                {t("createAccount")}
                <ArrowUpRight className="h-3.5 w-3.5" />
              </Link>
            </>
          )}
        </div>

        <button
          className="rounded-full p-2 md:hidden"
          onClick={() => setMobile((m) => !m)}
          data-testid="mobile-menu-toggle"
          style={{ color: "hsl(var(--ink))" }}
        >
          {mobile ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {mobile && (
        <div
          className="border-t md:hidden"
          style={{
            borderColor: "hsl(var(--border))",
            backgroundColor: "hsl(var(--surface))",
          }}
          data-testid="mobile-menu-panel"
        >
          <div className="mx-auto flex max-w-[1400px] flex-col gap-1 px-5 py-3">
            <Link to="/" onClick={() => setMobile(false)} className="rounded-lg px-3 py-2 text-sm hover:bg-[hsl(var(--surface-2))]">
              {t("home")}
            </Link>
            <Link to="/browse" onClick={() => setMobile(false)} className="rounded-lg px-3 py-2 text-sm hover:bg-[hsl(var(--surface-2))]">
              {t("browse")}
            </Link>
            {isAuth && (
              <>
                <Link to="/upload" onClick={() => setMobile(false)} className="rounded-lg px-3 py-2 text-sm hover:bg-[hsl(var(--surface-2))]">
                  {t("upload")}
                </Link>
                <Link to="/my-uploads" onClick={() => setMobile(false)} className="rounded-lg px-3 py-2 text-sm hover:bg-[hsl(var(--surface-2))]">
                  {t("myUploads")}
                </Link>
              </>
            )}
            <div className="my-1 h-px bg-[hsl(var(--border))]" />
            <button onClick={toggleTheme} className="flex items-center justify-between rounded-lg px-3 py-2 text-sm hover:bg-[hsl(var(--surface-2))]">
              <span>{theme === "dark" ? t("light") : t("dark")}</span>
              {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>
            <button onClick={toggleLang} className="flex items-center justify-between rounded-lg px-3 py-2 text-sm hover:bg-[hsl(var(--surface-2))]">
              <span>{lang === "en" ? "العربية" : "English"}</span>
              <Languages className="h-4 w-4" />
            </button>
            <div className="my-1 h-px bg-[hsl(var(--border))]" />
            {isAuth ? (
              <button
                onClick={async () => {
                  await logout();
                  setMobile(false);
                  nav("/");
                }}
                className="rounded-lg px-3 py-2 text-start text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30"
              >
                {t("signOut")}
              </button>
            ) : (
              <>
                <Link to="/login" onClick={() => setMobile(false)} className="rounded-lg px-3 py-2 text-sm hover:bg-[hsl(var(--surface-2))]">
                  {t("signIn")}
                </Link>
                <Link to="/register" onClick={() => setMobile(false)} className="btn btn-primary !justify-start !rounded-lg !px-3 !py-2 text-sm">
                  {t("createAccount")}
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
